// src/mocks/handlers/account/payoutAccountCreateHandler.ts

import { rest } from 'msw';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';
import payoutAccountsData from '@/mocks/data/payoutAccounts.json';
import {
  IPayoutAccountsNewRequestBody,
  IPayoutAccounts,
  IRequestPayoutAccounts,
} from '@/domains/account/payout_accounts/payoutAccounts.types';

const getCurrencySymbol = (currency: string): string | null => {
  const symbols: Record<string, string> = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    CHF: 'CHF',
  };
  return symbols[currency] || null;
};

const MOCK_UUID = 'mock-uuid';

// Transform API request to response format
const transformToResponse = (
  payload: IPayoutAccountsNewRequestBody,
): IPayoutAccounts => {
  return {
    uuid: MOCK_UUID,
    currency: payload.currency_code,
    currency_symbol: getCurrencySymbol(payload.currency_code),
    account_number: payload.account_number || null,
    sort_code: payload.sort_code || null,
    iban: payload.iban || null,
    swift_bic: payload.swift_bic || null,
    ach_routing_number: payload.ach_routing_number || null,
    account_holder: payload.account_holder,
    bank_country_code: payload.bank_country_code,
    country_code: payload.country_code,
    street_address: payload.street_address,
    city: payload.city,
    state: payload.state || null,
    province: payload.province || null,
    postcode: payload.postcode,
    active: true,
    verified: false,
  };
};

export const payoutAccountCreateHandler = rest.post<
  IPayoutAccountsNewRequestBody,
  IRequestPayoutAccounts
>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.ACCOUNT_PAYOUT_ACCOUNT_CREATE}`,
  async (req, res, ctx) => {
    const customer_uuid = req.params.customer_uuid as string;

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const payload = await req.json();

    const customerAccounts = payoutAccountsData.find(
      (entry) => entry.customer === customer_uuid,
    ) as { customer: string; results: IPayoutAccounts[] } | undefined;

    if (customerAccounts) {
      const existingAccount = customerAccounts.results.find(
        (account) =>
          account.currency === payload.currency_code && account.active === true,
      );

      if (existingAccount) {
        return res(
          ctx.status(400),
          ctx.json({ detail: 'Failed to create payout account' }),
        );
      }
    }

    const newAccount = transformToResponse(payload);

    if (customerAccounts) {
      customerAccounts.results.push(newAccount);
    } else {
      (
        payoutAccountsData as Array<{
          customer: string;
          results: IPayoutAccounts[];
        }>
      ).push({
        customer: customer_uuid,
        results: [newAccount],
      });
    }

    return res(
      ctx.status(200),
      ctx.json({ message: 'Payout account created successfully' }),
    );
  },
);
